import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [sortBy, setSortBy] = useState('latest');
  const category = getCategoryBySlug(slug || '');
  const comics = getComicsByCategory(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const handleCategoryChange = (catSlug: string) => {
    if (catSlug === slug) return;
    window.location.href = `/category/${catSlug}`;
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl">
              <i className="ri-emotion-sad-line text-foreground-400"></i>
            </span>
            <p className="mt-4 text-foreground-600">未找到该分类</p>
            <Link
              to="/"
              className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[260px] md:h-[340px] overflow-hidden pt-16">
          <img
            src={category.image}
            alt={`${category.name}漫画在线阅读 - 皮皮喵漫画`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading drop-shadow-lg">
              {category.name}
            </h1>
            <p className="mt-2 text-sm md:text-base text-white/80 max-w-lg drop-shadow-md">
              {category.description}
            </p>
            <p className="mt-1 text-xs text-white/60">
              共 {category.count}+ 部作品
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-800 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="w-full bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-2 overflow-x-auto">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sort & Content */}
        <section className="w-full bg-background-50 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-600">
                共 <strong>{comics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-secondary-100 text-secondary-700'
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedComics.length === 0 ? (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl">
                  <i className="ri-inbox-line text-foreground-300"></i>
                </span>
                <p className="mt-4 text-foreground-500">暂无漫画作品</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${category.name} - 皮皮喵漫画`,
            "description": category.description,
            "url": `https://pipimao.com/category/${category.slug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "皮皮喵漫画",
              "url": "https://pipimao.com"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://pipimao.com" },
                { "@type": "ListItem", "position": 2, "name": category.name, "item": `https://pipimao.com/category/${category.slug}` }
              ]
            }
          }),
        }}
      />
    </>
  );
}